/**
 * Application Navigation Utility
 * Handles step-wise navigation validation for applicant form tabs
 */

import type { ResidentApplicationData } from '@/types/application'

/**
 * Get tab display name for validation messages
 */
export function getTabDisplayName(tab: ApplicationTab): string {
  return TAB_NAMES[tab]
}

export const APPLICATION_TABS = [
  'about',
  'property',
  'income',
  'history',
  'signature',
  'payment'
] as const

export type ApplicationTab = typeof APPLICATION_TABS[number]

export const TAB_PATHS: Record<ApplicationTab, string> = {
  about: '/applicant/application/about',
  property: '/applicant/application/property',
  income: '/applicant/application/income',
  history: '/applicant/application/history',
  signature: '/applicant/application/signature',
  payment: '/applicant/application/payment'
}

/**
 * Get tab path with applicant ID (for resident-application routes)
 * Tab is stored in location state only; URL has no ?tab= query.
 */
export function getTabPathWithApplicantId(_tab: ApplicationTab, applicantId: string | number): string {
  return `/resident-application/${applicantId}`
}

/**
 * Get current tab from location (state only for resident-application; old path format for /applicant/application/:tab).
 */
export function getCurrentTabFromLocation(location: { pathname: string; state?: unknown }): ApplicationTab {
  const stateTab = (location.state as { tab?: ApplicationTab } | null)?.tab
  if (stateTab && APPLICATION_TABS.includes(stateTab)) return stateTab
  // Old format: path is /applicant/application/:tab
  const oldMatch = location.pathname.match(/^\/applicant\/application\/(\w+)$/)
  if (oldMatch) {
    const tab = oldMatch[1] as ApplicationTab
    if (APPLICATION_TABS.includes(tab)) return tab
  }
  return 'about'
}

/**
 * Get navigation options with tab in state
 */
export function getTabNavigationOptions(tab: ApplicationTab): { state: { tab: ApplicationTab }; replace?: boolean } {
  return {
    state: { tab },
    replace: false
  }
}

/**
 * Get all tab paths with applicant ID
 */
export function getTabPathsWithApplicantId(applicantId: string | number): Record<ApplicationTab, string> {
  return {
    about: getTabPathWithApplicantId('about', applicantId),
    property: getTabPathWithApplicantId('property', applicantId),
    income: getTabPathWithApplicantId('income', applicantId),
    history: getTabPathWithApplicantId('history', applicantId),
    signature: getTabPathWithApplicantId('signature', applicantId),
    payment: getTabPathWithApplicantId('payment', applicantId),
  }
}

export const TAB_NAMES: Record<ApplicationTab, string> = {
  about: 'About Yourself',
  property: 'Property Info',
  income: 'Income and Employment',
  history: 'Personal History',
  signature: 'Acknowledgements and Identity',
  payment: 'Payment Info'
}

/**
 * Check if a tab is complete/valid based on application data
 * Matches Angular's form validation logic exactly
 */
export function isTabComplete(
  tab: ApplicationTab,
  applicationData: ResidentApplicationData | null | undefined
): boolean {
  if (!applicationData) return false

  switch (tab) {
    case 'about': {
      // Angular required form controls: first_name, last_name, birthdate, security_number, mobile_no
      // Plus current_address (FormGroup with its own controls)
      // The API response may use different field names (e.g. applicant_first_name vs first_name)
      const firstName = applicationData.first_name || applicationData.applicant_first_name ||
        applicationData.user_data?.first_name
      const lastName = applicationData.last_name || applicationData.applicant_last_name ||
        applicationData.user_data?.last_name
      const mobileNo = applicationData.mobile_no || applicationData.applicant_mobile_no ||
        applicationData.user_data?.mobile_no
      const birthdate = applicationData.birthdate
      const ssn = applicationData.security_number
      const currentAddress = applicationData.current_address

      // Current address just needs to exist as a saved object.
      // The API returns current_address once the user has saved the address form.
      // We check for street_address or house_no or city to confirm it's a real saved address
      // (not just an empty object).
      const hasValidAddress = currentAddress && (
        currentAddress.house_no ||
        currentAddress.street_address ||
        currentAddress.city ||
        currentAddress.street
      )

      const isComplete = !!(
        firstName &&
        lastName &&
        mobileNo &&
        birthdate &&
        ssn &&
        hasValidAddress
      )

      // Debug: log which field is causing About tab to not show as complete
      if (!isComplete && typeof window !== 'undefined' && (window as { __DEV__?: boolean }).__DEV__ !== false) {
        const missing: string[] = []
        if (!firstName) missing.push('firstName (first_name / applicant_first_name / user_data.first_name)')
        if (!lastName) missing.push('lastName (last_name / applicant_last_name / user_data.last_name)')
        if (!mobileNo) missing.push('mobileNo (mobile_no / applicant_mobile_no / user_data.mobile_no)')
        if (!birthdate) missing.push('birthdate')
        if (!ssn) missing.push('security_number')
        if (!hasValidAddress) missing.push('current_address (needs house_no, street_address, city, or street)')
        if (missing.length > 0) {
          console.warn('[Tab Completion] About tab incomplete. Missing fields:', missing.join(', '))
        }
      }

      return isComplete
    }

    case 'property': {
      // Angular required: applied_property, property_id, rent_amount, lease_start_date, lease_duration, other_individuals
      // Property can come from applicationData.property or direct fields
      const propertyId = applicationData.property_id || applicationData.property?.property_id
      const rentAmount = applicationData.rent_amount
      const leaseStartDate = applicationData.lease_start_date
      const leaseDuration = applicationData.lease_duration

      // For stepper display, we check the core property fields
      // other_individuals is required but may not be set until form submission
      return !!(
        propertyId &&
        rentAmount != null && String(rentAmount).trim() !== '' && Number(rentAmount) > 0 &&
        leaseStartDate &&
        leaseDuration
      )
    }

    case 'income': {
      // Angular: employment_status (required) and previous_employer (required, default '0')
      // Form is valid when these are set, regardless of employment arrays
      const employmentStatus = applicationData.employment_status

      // If employment_status is not set at all, tab is incomplete
      if (employmentStatus === null || employmentStatus === undefined || employmentStatus === '') {
        return false
      }

      // Angular: When employment_status is set (any value including '0'), 
      // the base form is valid. The FormArrays are conditionally added.
      // employment_status === '0' means unemployed - form is valid
      // employment_status === '1' means employed - FormArray is added but may be empty initially

      // For stepper display purposes, if employment_status is set, consider it complete
      // This matches Angular where the form is valid once required fields are filled
      return true
    }

    case 'history': {
      // Angular required: rent_debt_collection, involved_bankruptcy, children
      const rentDebtCollection = applicationData.rent_debt_collection
      const involvedBankruptcy = applicationData.involved_bankruptcy
      const children = applicationData.children

      // Each field must have a value (can be '0' or '1', or a number, but not null/undefined/'')
      const hasRentDebt = rentDebtCollection !== null && rentDebtCollection !== undefined && rentDebtCollection !== ''
      const hasBankruptcy = involvedBankruptcy !== null && involvedBankruptcy !== undefined && involvedBankruptcy !== ''
      const hasChildren = children !== null && children !== undefined && children !== ''

      return hasRentDebt && hasBankruptcy && hasChildren
    }

    case 'signature': {
      // Angular required: access_statement, goverment_id_type
      // If not digital signature: signature and signaturetype also required
      // For completion display, we check access_statement and goverment_id_type
      const accessStatement = applicationData.access_statement
      const govIdType = applicationData.goverment_id_type
      const digitalSignature = applicationData.digital_signature

      // Must have access_statement set to '1' and goverment_id_type selected
      // Plus digital_signature (which contains the signature JSON)
      const hasAccessStatement = accessStatement === '1'
      const hasGovIdType = govIdType !== null && govIdType !== undefined && govIdType !== ''
      const hasSignature = !!digitalSignature

      return hasAccessStatement && hasGovIdType && hasSignature
    }

    case 'payment': {
      // Payment is complete when payment has been made
      // Angular checks payment_log_id to determine if payment was added
      return !!(
        applicationData.payment_log_id ||
        applicationData.rent_added === true ||
        (applicationData.paid_amount && Number(applicationData.paid_amount) > 0)
      )
    }

    default:
      return false
  }
}

/**
 * Get the first incomplete tab index
 */
export function getFirstIncompleteTabIndex(
  applicationData: ResidentApplicationData | null | undefined
): number {
  for (let i = 0; i < APPLICATION_TABS.length; i++) {
    const tab = APPLICATION_TABS[i]
    if (!isTabComplete(tab, applicationData)) {
      return i
    }
  }
  return APPLICATION_TABS.length // All tabs complete
}

/**
 * Check if a tab can be accessed (all previous tabs must be complete)
 * @param targetTabIndex - Index of target tab
 * @param applicationData - Application data
 * @param isReadOnly - Whether application is read-only
 * @param _isPaymentAdded - Whether payment has been added (deprecated - kept for backward compatibility)
 * @param _fromRoute - Whether user came from route (deprecated - kept for backward compatibility)
 */
export function canAccessTab(
  targetTabIndex: number,
  applicationData: ResidentApplicationData | null | undefined,
  isReadOnly: boolean = false,
  _isPaymentAdded: boolean = false,
  _fromRoute: boolean = true
): { canAccess: boolean; reason?: string; firstIncompleteTab?: number } {
  // Read-only mode: allow navigation to any tab
  if (isReadOnly) {
    return { canAccess: true }
  }

  // Property-listed blocking: matches Angular's changeTabView guard (line 1790).
  // When the property is not listed, only About (0) and Property (1) tabs are accessible.
  if (targetTabIndex > 1 && applicationData) {
    const isPropertyListed = applicationData.property?.listed === '1'
    if (!isPropertyListed) {
      return {
        canAccess: false,
        reason: `Please ensure your property is listed before proceeding.`,
        firstIncompleteTab: 1
      }
    }
  }

  // STRICT STEP-WISE NAVIGATION: Always enforce step-wise navigation for ALL tabs including payment
  // Check all previous tabs are complete before allowing access to target tab
  for (let i = 0; i < targetTabIndex; i++) {
    const tab = APPLICATION_TABS[i]
    if (!isTabComplete(tab, applicationData)) {
      return {
        canAccess: false,
        reason: `Please complete "${TAB_NAMES[tab]}" before proceeding.`,
        firstIncompleteTab: i
      }
    }
  }

  return { canAccess: true }
}

/**
 * Get tab index from path and location state.
 * Supports old format (/applicant/application/:tab) and new format (/resident-application/:applicantId with state).
 */
export function getTabIndexFromPath(path: string, locationState?: { tab?: ApplicationTab } | null, _search?: string): number {
  const tab = getTabFromPath(path, locationState)
  return tab ? APPLICATION_TABS.indexOf(tab) : -1
}

/**
 * Get tab from path and location state (no ?tab= in URL).
 */
export function getTabFromPath(path: string, locationState?: { tab?: ApplicationTab } | null, _search?: string): ApplicationTab | null {
  // Try old format first (path is full path like /applicant/application/about)
  let tab = APPLICATION_TABS.find(t => TAB_PATHS[t] === path) ?? null

  // New format: /resident-application/:applicantId — tab from state only
  // `active` is a session alias (Angular parity) resolved to a numeric id in ResidentApplication.
  if (!tab && path.match(/^\/resident-application\/(\d+|active)$/)) {
    if (locationState?.tab && APPLICATION_TABS.includes(locationState.tab)) {
      tab = locationState.tab
    } else {
      tab = 'about'
    }
  }

  return tab
}

/**
 * Result of server-side payment validation
 */
export interface PaymentValidationResult {
  isValid: boolean
  missingFields: string[]
  message: string
  redirectTab: ApplicationTab
}

/**
 * Validate application data from the server before allowing payment tab access.
 * Matches Angular's validateApplication() in resident-application-add.component.ts
 * which GETs /api/resident_application/{id} and checks 5 required fields before
 * allowing the user into the Payment tab.
 */
export function validateApplicationForPayment(
  serverData: ResidentApplicationData
): PaymentValidationResult {
  const missingFields: string[] = []
  let redirectTab: ApplicationTab = 'signature'

  if (!(serverData.current_address && serverData.current_address.house_no)) {
    missingFields.push('About Yourself - Current Address')
    if (missingFields.length === 1) redirectTab = 'about'
  }
  if (!serverData.security_number) {
    missingFields.push('About Yourself - Social Security number')
    if (missingFields.length === 1) redirectTab = 'about'
  }
  if (!(serverData.property_id || (serverData.property && serverData.property.property_id))) {
    missingFields.push('Property Info')
    if (missingFields.length === 1) redirectTab = 'property'
  }
  if (!serverData.employment_status) {
    missingFields.push('Income and Employment Info')
    if (missingFields.length === 1) redirectTab = 'income'
  }
  if (!serverData.digital_signature) {
    missingFields.push('Signature Info')
    if (missingFields.length === 1) redirectTab = 'signature'
  }

  if (missingFields.length > 0) {
    return {
      isValid: false,
      missingFields,
      redirectTab,
      message: `Please Save Following Info:\n${missingFields.join(', ')}\nTo Continue with Application Payment`
    }
  }

  return { isValid: true, missingFields: [], redirectTab: 'payment', message: '' }
}

